import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  FunnelIcon,
  PaperAirplaneIcon,
  PencilIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Quote, QuoteStatus, quotesAPI } from '../../services/api';
import { Badge, Button, Card, CardBody, EmptyState, PageHero, Spinner } from '../ui';
import QuoteModal from '../deals/QuoteModal';
import QuoteDetailModal from '../deals/QuoteDetailModal';
import QuoteSendModal from '../deals/QuoteSendModal';

const QUOTE_STATUSES: QuoteStatus[] = ['DRAFT', 'SENT', 'ACCEPTED', 'REJECTED', 'EXPIRED'];

const statusVariant: Record<QuoteStatus, 'primary' | 'success' | 'warning' | 'error' | 'neutral'> = {
  DRAFT: 'neutral',
  SENT: 'primary',
  ACCEPTED: 'success',
  REJECTED: 'error',
  EXPIRED: 'warning',
};

const formatStatus = (status: string) => status.replace('_', ' ');

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatCurrency = (amount: number, currency: string) => {
  const numericAmount = Number(amount || 0);
  return `${currency || 'USD'} ${numericAmount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const SalesmanQuotesPage: React.FC = () => {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [statusFilter, setStatusFilter] = useState<QuoteStatus | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [editingQuote, setEditingQuote] = useState<Quote | null>(null);
  const [selectedQuoteId, setSelectedQuoteId] = useState<number | null>(null);
  const [quoteToSend, setQuoteToSend] = useState<Quote | null>(null);
  const [downloadingQuoteId, setDownloadingQuoteId] = useState<number | null>(null);

  const loadQuotes = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await quotesAPI.getAll({
        status: statusFilter || undefined,
      });
      setQuotes(data);
    } catch (loadError: any) {
      setError(loadError.response?.data?.message || 'Failed to load quotes');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void loadQuotes();
  }, [loadQuotes]);

  const quoteSummary = useMemo(() => {
    return {
      total: quotes.length,
      draft: quotes.filter((quote) => quote.status === 'DRAFT').length,
      sent: quotes.filter((quote) => quote.status === 'SENT').length,
      accepted: quotes.filter((quote) => quote.status === 'ACCEPTED').length,
    };
  }, [quotes]);

  const showMessage = (nextMessage: string) => {
    setMessage(nextMessage);
    window.setTimeout(() => setMessage(''), 3000);
  };

  const handleDownloadQuote = async (quoteId: number) => {
    try {
      setDownloadingQuoteId(quoteId);
      setError('');
      await quotesAPI.downloadPdf(quoteId);
    } catch (downloadError: any) {
      setError(downloadError.response?.data?.message || 'Failed to download quote PDF');
    } finally {
      setDownloadingQuoteId(null);
    }
  };

  const clearFilters = () => {
    setStatusFilter('');
  };

  return (
    <div className="space-y-6">
      <PageHero
        title="Quotes"
        subtitle="Client quotes and follow-up status"
        accentColor="cyan"
        icon={<DocumentDuplicateIcon className="h-6 w-6 text-white" />}
      >
        <Button
          type="button"
          variant="primary"
          leftIcon={<PlusIcon className="h-5 w-5" />}
          onClick={() => {
            setEditingQuote(null);
            setShowQuoteModal(true);
          }}
        >
          Create Quote
        </Button>
      </PageHero>

      {error && (
        <div className="rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700">
          {error}
        </div>
      )}

      {message && (
        <div className="rounded-lg border border-success-200 bg-success-50 px-4 py-3 text-sm text-success-700">
          {message}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-lg border border-neutral-200 bg-white px-4 py-3">
          <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">Total</div>
          <div className="mt-1 text-2xl font-semibold text-neutral-900">{quoteSummary.total}</div>
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white px-4 py-3">
          <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">Draft</div>
          <div className="mt-1 text-2xl font-semibold text-neutral-900">{quoteSummary.draft}</div>
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white px-4 py-3">
          <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">Sent</div>
          <div className="mt-1 text-2xl font-semibold text-neutral-900">{quoteSummary.sent}</div>
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white px-4 py-3">
          <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">Accepted</div>
          <div className="mt-1 text-2xl font-semibold text-neutral-900">{quoteSummary.accepted}</div>
        </div>
      </div>

      <Card variant="bordered" padding="md">
        <CardBody>
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <label htmlFor="quote-status-filter" className="flex items-center text-sm font-medium text-neutral-700">
                <FunnelIcon className="mr-2 h-4 w-4 text-neutral-500" />
                Status
              </label>
              <select
                id="quote-status-filter"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as QuoteStatus | '')}
                className="block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 md:max-w-xs"
              >
                <option value="">All statuses</option>
                {QUOTE_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {formatStatus(status)}
                  </option>
                ))}
              </select>
            </div>
            <Button
              type="button"
              variant="ghost"
              leftIcon={<XMarkIcon className="h-4 w-4" />}
              onClick={clearFilters}
              disabled={!statusFilter}
            >
              Clear
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card variant="default" padding="none" className="overflow-hidden">
        {loading ? (
          <div className="flex min-h-[260px] items-center justify-center">
            <Spinner size="lg" />
          </div>
        ) : quotes.length === 0 ? (
          <EmptyState
            type="document"
            title="No quotes found"
            description={statusFilter ? 'Try another status.' : 'No accessible quotes yet.'}
            action={
              <Button
                type="button"
                variant="primary"
                leftIcon={<PlusIcon className="h-4 w-4" />}
                onClick={() => {
                  setEditingQuote(null);
                  setShowQuoteModal(true);
                }}
              >
                Create Quote
              </Button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Quote
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Items
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Valid Until
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 bg-white">
                {quotes.map((quote) => (
                  <tr key={quote.id} className="hover:bg-neutral-50">
                    <td className="whitespace-nowrap px-6 py-4">
                      <button
                        type="button"
                        onClick={() => setSelectedQuoteId(quote.id)}
                        className="text-sm font-semibold text-primary-700 hover:text-primary-900"
                      >
                        {quote.quoteNumber}
                      </button>
                      {quote.revisionNumber > 0 && (
                        <div className="text-xs text-neutral-500">Revision {quote.revisionNumber}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="max-w-[220px] truncate text-sm font-medium text-neutral-900">
                        {quote.client?.name || `Client #${quote.clientId}`}
                      </div>
                      {quote.client?.email && (
                        <div className="max-w-[220px] truncate text-xs text-neutral-500">{quote.client.email}</div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <Badge variant={statusVariant[quote.status]}>{formatStatus(quote.status)}</Badge>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-semibold text-neutral-900">
                      {formatCurrency(quote.total, quote.currency)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-neutral-700">
                      {quote.items?.length || 0}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-neutral-700">
                      {formatDate(quote.validUntil || quote.expiresAt)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-neutral-700">
                      {formatDate(quote.createdAt)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="xs"
                          leftIcon={<EyeIcon className="h-4 w-4" />}
                          onClick={() => setSelectedQuoteId(quote.id)}
                        >
                          View
                        </Button>
                        {quote.status !== 'ACCEPTED' && (
                          <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            leftIcon={<PencilIcon className="h-4 w-4" />}
                            onClick={() => {
                              setEditingQuote(quote);
                              setShowQuoteModal(true);
                            }}
                          >
                            Edit
                          </Button>
                        )}
                        {quote.status === 'DRAFT' && (
                          <Button
                            type="button"
                            variant="primary"
                            size="xs"
                            leftIcon={<PaperAirplaneIcon className="h-4 w-4" />}
                            onClick={() => setQuoteToSend(quote)}
                          >
                            Send
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
                          isLoading={downloadingQuoteId === quote.id}
                          onClick={() => handleDownloadQuote(quote.id)}
                        >
                          PDF
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showQuoteModal && (
        <QuoteModal
          quote={editingQuote || undefined}
          onClose={() => {
            setShowQuoteModal(false);
            setEditingQuote(null);
          }}
          onSuccess={() => {
            setShowQuoteModal(false);
            showMessage(editingQuote ? 'Quote updated' : 'Quote created');
            setEditingQuote(null);
            void loadQuotes();
          }}
        />
      )}

      {selectedQuoteId && (
        <QuoteDetailModal
          quoteId={selectedQuoteId}
          onClose={() => setSelectedQuoteId(null)}
          onEdit={(quote) => {
            setSelectedQuoteId(null);
            setEditingQuote(quote);
            setShowQuoteModal(true);
          }}
          onRefresh={() => {
            void loadQuotes();
          }}
        />
      )}

      <QuoteSendModal
        quote={quoteToSend}
        onClose={() => setQuoteToSend(null)}
        onSent={async () => {
          showMessage('Quote sent');
          await loadQuotes();
        }}
      />
    </div>
  );
};

export default SalesmanQuotesPage;
